const SPEECH_DB_NAME = 'dirtylingo-speech';
const SPEECH_DB_VERSION = 2;
const SPEECH_STORE = 'speech-cache';
const SPEECH_CACHE_MAX_BYTES = 50 * 1024 * 1024;
const SPEECH_META_KEY = '__meta__' as const;

let speechDbPromise: Promise<IDBDatabase> | null = null;

type SpeechCacheEntry = {
  key: string;
  buffer?: ArrayBuffer;
  mime?: string;
  blob?: Blob;
  size: number;
  usedAt: number;
};

type SpeechCacheMeta = {
  key: typeof SPEECH_META_KEY;
  totalBytes: number;
  updatedAt: number;
};

const requestPromise = <T>(request: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const transactionPromise = (transaction: IDBTransaction): Promise<void> =>
  new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });

const openSpeechDb = (): Promise<IDBDatabase> => {
  if (!speechDbPromise) {
    speechDbPromise = new Promise((resolve, reject) => {
      if (!('indexedDB' in window)) {
        reject(new Error('IndexedDB not available'));
        return;
      }
      const request = indexedDB.open(SPEECH_DB_NAME, SPEECH_DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(SPEECH_STORE)) {
          const store = db.createObjectStore(SPEECH_STORE, { keyPath: 'key' });
          store.createIndex('usedAt', 'usedAt', { unique: false });
          return;
        }
        const store = request.transaction?.objectStore(SPEECH_STORE);
        if (store && !store.indexNames.contains('usedAt')) {
          store.createIndex('usedAt', 'usedAt', { unique: false });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }
  return speechDbPromise;
};

const getSpeechMeta = async (db: IDBDatabase): Promise<SpeechCacheMeta | null> => {
  const transaction = db.transaction(SPEECH_STORE, 'readonly');
  const store = transaction.objectStore(SPEECH_STORE);
  const result = await requestPromise(store.get(SPEECH_META_KEY));
  return (result as SpeechCacheMeta | undefined) ?? null;
};

const setSpeechMeta = async (db: IDBDatabase, meta: SpeechCacheMeta): Promise<void> => {
  const transaction = db.transaction(SPEECH_STORE, 'readwrite');
  const store = transaction.objectStore(SPEECH_STORE);
  await requestPromise(store.put(meta));
  await transactionPromise(transaction);
};

const putSpeechEntry = async (db: IDBDatabase, entry: SpeechCacheEntry): Promise<void> => {
  const transaction = db.transaction(SPEECH_STORE, 'readwrite');
  const store = transaction.objectStore(SPEECH_STORE);
  await requestPromise(store.put(entry));
  await transactionPromise(transaction);
};

const ensureSpeechMeta = async (db: IDBDatabase): Promise<SpeechCacheMeta> => {
  const existing = await getSpeechMeta(db);
  if (existing) {
    return existing;
  }
  let totalBytes = 0;
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(SPEECH_STORE, 'readonly');
    const store = transaction.objectStore(SPEECH_STORE);
    const request = store.openCursor();
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) {
        resolve();
        return;
      }
      const value = cursor.value as SpeechCacheEntry | SpeechCacheMeta;
      if (value.key !== SPEECH_META_KEY) {
        const entry = value as SpeechCacheEntry;
        const size = entry.size ?? entry.blob?.size ?? 0;
        totalBytes += size;
      }
      cursor.continue();
    };
    request.onerror = () => reject(request.error);
  });
  const meta: SpeechCacheMeta = { key: SPEECH_META_KEY, totalBytes, updatedAt: Date.now() };
  await setSpeechMeta(db, meta);
  return meta;
};

const evictSpeechCache = async (db: IDBDatabase, incomingSize: number): Promise<void> => {
  const meta = await ensureSpeechMeta(db);
  let totalBytes = meta.totalBytes;
  if (totalBytes + incomingSize <= SPEECH_CACHE_MAX_BYTES) {
    return;
  }
  const remainingTarget = SPEECH_CACHE_MAX_BYTES - incomingSize;
  const transaction = db.transaction(SPEECH_STORE, 'readwrite');
  const store = transaction.objectStore(SPEECH_STORE);
  const index = store.index('usedAt');
  await new Promise<void>((resolve, reject) => {
    const request = index.openCursor();
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) {
        resolve();
        return;
      }
      const entry = cursor.value as SpeechCacheEntry;
      if (totalBytes <= remainingTarget) {
        resolve();
        return;
      }
      const entrySize = entry.size ?? entry.blob?.size ?? 0;
      totalBytes -= entrySize;
      cursor.delete();
      cursor.continue();
    };
    request.onerror = () => reject(request.error);
  });
  await transactionPromise(transaction);
  await setSpeechMeta(db, { key: SPEECH_META_KEY, totalBytes, updatedAt: Date.now() });
};

const getSpeechCacheEntry = async (key: string): Promise<SpeechCacheEntry | null> => {
  try {
    const db = await openSpeechDb();
    const transaction = db.transaction(SPEECH_STORE, 'readonly');
    const store = transaction.objectStore(SPEECH_STORE);
    const result = await requestPromise(store.get(key));
    return (result as SpeechCacheEntry | undefined) ?? null;
  } catch {
    return null;
  }
};

export const getSpeechCache = async (
  key: string
): Promise<{ buffer: ArrayBuffer; mime: string } | null> => {
  try {
    const entry = await getSpeechCacheEntry(key);
    if (entry?.buffer) {
      return { buffer: entry.buffer, mime: entry.mime ?? 'audio/mpeg' };
    }
    return null;
  } catch {
    return null;
  }
};

export const touchSpeechCache = async (key: string): Promise<void> => {
  try {
    const db = await openSpeechDb();
    const entry = await getSpeechCacheEntry(key);
    if (!entry || !entry.buffer) {
      return;
    }
    await putSpeechEntry(db, { ...entry, usedAt: Date.now() });
  } catch {
    return;
  }
};

export const setSpeechCache = async (key: string, blob: Blob): Promise<void> => {
  try {
    const db = await openSpeechDb();
    const existing = await getSpeechCacheEntry(key);
    const existingSize = existing?.size ?? 0;
    const buffer = await blob.arrayBuffer();
    const incomingDelta = Math.max(buffer.byteLength - existingSize, 0);
    await evictSpeechCache(db, incomingDelta);
    const meta = await ensureSpeechMeta(db);
    await putSpeechEntry(db, {
      key,
      buffer,
      mime: blob.type,
      size: buffer.byteLength,
      usedAt: Date.now(),
    });
    const updatedTotal = meta.totalBytes - existingSize + buffer.byteLength;
    await setSpeechMeta(db, { key: SPEECH_META_KEY, totalBytes: updatedTotal, updatedAt: Date.now() });
  } catch {
    return;
  }
};
